/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import dayjs from "dayjs";
import { Row, Col } from "react-awesome-styled-grid";
import { useForm } from "react-hook-form";
import { If, Then } from "react-if";
import styled from "styled-components";
import {
  Box,
  Button,
  Label,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { DatePicker } from "~/components/DatePicker/DatePicker";
import { useShowError } from "~/components/Error/useShowError";
import { showSuccess } from "~/components/Toast";
import { DetailRow } from "~/components/Utils/DetailRow";
import { dateFormat } from "~/constants";
import { Environment } from "~/models";
import {
  checkDates,
  displayEnvType,
  generateActionSuccessText,
  useAxios,
} from "~/tools";
import { InlineDropdownEnvStatus } from "./InlineDropdownEnvStatus";
import { InlineServiceLevelEnv } from "./InlineServiceLevelEnv";

type EnvironmentDetailsInfoProps = {
  environment: Environment;
  setDateError: React.Dispatch<React.SetStateAction<boolean>>;
  dateError: boolean;
  fetchEnvironment: () => Promise<void>;
  canEdit?: boolean;
};

const StyledLabel = styled(Label)`
  min-width: 12rem;
  word-break: break-all;
  color: ${theme.color.text.text02};
  padding: initial;
  text-transform: ${theme.typography.titleCase};
`;

// eslint-disable-next-line sonarjs/cognitive-complexity
function EnvironmentDetailsInfo({
  environment,
  setDateError,
  dateError,
  fetchEnvironment,
  canEdit = true,
}: EnvironmentDetailsInfoProps) {
  const { showError } = useShowError();
  const { Axios } = useAxios();
  const [isChange, setIsChange] = React.useState(false);

  const { register, setValue, handleSubmit } = useForm();

  const onSubmit = async (values: Environment) => {
    const organizeValues = {
      timeline: {
        softLaunch: checkDates(dates?.softLaunch),
        goLive: checkDates(dates?.goLive),
        offboarding: checkDates(dates?.offboarding),
      },
      attachedServiceLevel: environment.attachedServiceLevel?.serviceLevel?.id
        ? {
            capacityBasedFeeType:
              environment.attachedServiceLevel?.capacityBasedFeeType,
            serviceLevelId: environment.attachedServiceLevel?.serviceLevel?.id,
            capacityBasedPercentageFee:
              environment.attachedServiceLevel?.capacityBasedPercentageFee,
          }
        : null,
      billingRules: {
        serviceRequests: {
          officeHours: {
            pricePerHour:
              environment.billingRules.serviceRequests.officeHours
                ?.pricePerHour,
          },
          outsideOfficeHoursAfterMidnight:
            environment.billingRules.serviceRequests
              .outsideOfficeHoursAfterMidnight,
          outsideOfficeHoursBeforeMidnight:
            environment.billingRules.serviceRequests
              .outsideOfficeHoursBeforeMidnight,
        },
        incidents: {
          officeHours: environment.billingRules.incidents?.officeHours,
          outsideOfficeHoursBeforeMidnight:
            environment.billingRules.incidents
              ?.outsideOfficeHoursBeforeMidnight,
          outsideOfficeHoursAfterMidnight:
            environment.billingRules.incidents?.outsideOfficeHoursAfterMidnight,
        },
      },
      status: values.status || environment.status,
      cfRepoPath: environment.cfRepoPath,
      name: environment.name,
    };
    try {
      await Axios().put(`/environments/${environment.id}`, organizeValues);
      showSuccess(
        generateActionSuccessText(`Environment ${environment.name}`)()(
          "updated"
        )()
      );
    } catch (e) {
      showError(e.response);
    } finally {
      fetchEnvironment();
    }
  };
  const [dates, setDates] = React.useState({
    softLaunch: "",
    goLive: "",
    offboarding: "",
  });

  React.useEffect(() => {
    environment &&
      setDates({
        softLaunch: environment.timeline.softLaunch || "",
        goLive: environment.timeline.goLive || "",
        offboarding: environment.timeline.offboarding || "",
      });
  }, [environment, setDates]);

  React.useEffect(() => {
    dayjs(dates.offboarding).isBefore(dayjs(dates.softLaunch)) ||
    dayjs(dates.softLaunch).isAfter(dayjs(dates.goLive)) ||
    dayjs(dates.offboarding).isBefore(dayjs(dates.goLive))
      ? setDateError(true)
      : setDateError(false);

    if (
      (dates.offboarding !== "" &&
        dates.offboarding !== environment.timeline.offboarding) ||
      (dates.softLaunch !== "" &&
        dates.softLaunch !== environment.timeline.softLaunch) ||
      (dates.goLive !== "" && dates.goLive !== environment.timeline.goLive)
    ) {
      return setIsChange(true);
    }

    return setIsChange(false);
  }, [
    dates,
    environment.timeline,
    environment.timeline.goLive,
    environment.timeline.offboarding,
    environment.timeline.softLaunch,
    setDateError,
  ]);

  const cancel = () => {
    setDates(() => ({
      softLaunch: environment.timeline.softLaunch || "",
      goLive: environment.timeline.goLive || "",
      offboarding: environment.timeline.offboarding || "",
    }));
  };

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={8}>
          <If condition={!environment?.generalMapping}>
            <Then>
              <Text weight="medium">Environment Details</Text>
              <DetailRow title="Status">
                <InlineDropdownEnvStatus
                  item={environment}
                  onSubmit={onSubmit}
                  canEdit={canEdit}
                />
              </DetailRow>

              <DetailRow title="Type">
                {displayEnvType(environment?.type || "")}
              </DetailRow>
            </Then>
          </If>

          <DetailRow title="Service level">
            <InlineServiceLevelEnv
              environment={environment}
              refetch={fetchEnvironment}
              canEdit={canEdit}
            />
          </DetailRow>

          <FlexContainer>
            <FlexContainer direction="column" align="flex-start">
              <Spacer height=".5rem" />
              <FlexContainer align="center">
                <StyledLabel name="Soft Launch At:" required />
                <DatePicker
                  name="softLaunch"
                  register={register}
                  setDates={setDates}
                  dates={dates}
                  setValue={setValue}
                  canEdit={canEdit}
                  mandatoryDate
                />
              </FlexContainer>
              <Spacer height=".5rem" />
              <FlexContainer align="center">
                <StyledLabel name="Go live At:" required />
                <DatePicker
                  name="goLive"
                  register={register}
                  setDates={setDates}
                  dates={dates}
                  setValue={setValue}
                  canEdit={canEdit}
                  mandatoryDate
                />
              </FlexContainer>
              <Spacer height=".5rem" />
              <FlexContainer align="center">
                <StyledLabel name="Offboarding At:" />
                <DatePicker
                  name="offboarding"
                  register={register}
                  setDates={setDates}
                  dates={dates}
                  setValue={setValue}
                  canEdit={canEdit}
                />
              </FlexContainer>
            </FlexContainer>

            <FlexContainer align="center" justify="center" height="100%">
              {isChange && (
                <>
                  <Button
                    aria-label="Save environment details dates"
                    icon="save"
                    size="md"
                    ml="8px"
                    severity="low"
                    disabled={dateError}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Save
                  </Button>
                  <Button
                    ml=".5rem"
                    icon="close"
                    size="md"
                    severity="low"
                    onClick={cancel}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </FlexContainer>
          </FlexContainer>
          {dateError && (
            <Message
              css={{ margin: "0.5rem 0 1rem 0" }}
              status="danger"
              image="danger"
            >
              The offboarding date needs to be after the soft launch and go-live
              date.
            </Message>
          )}
        </Col>

        <Col>
          <Box boxStyle="lightGrey">
            <FlexContainer>
              <Text>Modified By: </Text>
              <Text css={{ marginLeft: "1rem" }}>
                {environment?.log?.modified?.author
                  ? environment?.log?.modified?.author
                  : "-"}
              </Text>
            </FlexContainer>
            <FlexContainer>
              <Text>Modified At: </Text>
              <Text css={{ marginLeft: "1rem" }}>
                {environment?.log?.modified?.time
                  ? dayjs(environment?.log?.modified?.time).format(dateFormat)
                  : "-"}
              </Text>
            </FlexContainer>
          </Box>
        </Col>
      </Row>
    </Box>
  );
}

export { EnvironmentDetailsInfo };
